import { useState } from 'react';
import { createCheckoutSession, type CreateCheckoutSessionResult } from '@/lib/createCheckoutSession';
import type { PlanInterval } from '@/lib/subscriptionPlan';

function getCheckoutErrorMessage(result: Extract<CreateCheckoutSessionResult, { ok: false }>) {
  if (result.reason === 'not_authenticated') {
    return 'You are not authenticated. Please sign in again.';
  }

  if (result.reason === 'invoke_failed') {
    return 'Failed to create checkout session. Please try again.';
  }

  if (result.reason === 'missing_url') {
    return 'Checkout URL missing. Please contact support.';
  }

  return 'An unexpected error occurred. Please try again.';
}

export function useCheckout() {
  const [loadingInterval, setLoadingInterval] = useState<PlanInterval | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startCheckout = async (interval: PlanInterval) => {
    setLoadingInterval(interval);
    setError(null);

    try {
      const checkoutResult = await createCheckoutSession(interval);

      if (checkoutResult.ok) {
        window.location.href = checkoutResult.url;
        return;
      }

      const checkoutFailure = checkoutResult as Extract<CreateCheckoutSessionResult, { ok: false }>;
      console.error('Checkout error:', checkoutFailure.error);
      setError(getCheckoutErrorMessage(checkoutFailure));
    } catch (err) {
      console.error('Checkout invocation error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoadingInterval(null);
    }
  };

  return {
    error,
    loadingInterval,
    startCheckout,
  };
}
